// This file is borrowed from the old ngx-cropperjs plugin, which just created an Angular wrapper component for cropperjs
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import * as Cropper from 'cropperjs/dist/cropper';



/*--------  Interface: Image cropper setting  --------*/


export interface ImageCropperSetting {
	width: number;
	height: number;
}


/*--------  Interface: Image Cropper Rresult  --------*/


export interface ImageCropperResult {
	imageData: Cropper.ImageData;
	cropData: Cropper.CropBoxData;
	blob?: Blob;
	dataUrl?: string;
}


/*--------  Component  --------*/


@Component({
	selector: 'ac-cropper',
	templateUrl: './cropper.component.html',
	styleUrls: ['./cropper.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class CropperComponent {


	/*--------  View child elements  --------*/


	@ViewChild('image', { static: false }) public image: ElementRef;


	/*--------  Input properties  --------*/


	@Input() public imageUrl: any;
	@Input() public settings: ImageCropperSetting;
	@Input() public cropbox: Cropper.CropBoxData;
	@Input() public loadImageErrorText: string;
	@Input() public cropperOptions: any;


	/*--------  Output events  --------*/


	@Output() public export = new EventEmitter<ImageCropperResult>();
	@Output() public ready = new EventEmitter();


	/*--------  Properties  --------*/


	public isLoading: boolean = true;
	public cropper: Cropper;
	public imageElement: HTMLImageElement;
	public loadError: any;
	public lastImgSrc: string;

	/*--------  Controller  --------*/

	/**
     * Image loaded event
     * @param ev 
     */
	public imageLoaded(ev: Event): void {

		//
		// Unset load error state
		this.loadError = false;

		//
		// Setup image element
		const image = ev.target as HTMLImageElement;
		this.imageElement = image;

		// 
		// Add crossOrigin?
		if (this.cropperOptions.checkCrossOrigin) { image.crossOrigin = 'anonymous'; }

		//
		// Image on ready event
		image.addEventListener('ready', () => {
			//
			// Emit ready
			this.ready.emit(true);

			//
			// Unset loading state
			this.isLoading = false;

			//
			// Validate cropbox existance
			if (this.cropbox) {

				//
				// Set cropbox data
				this.cropper.setCropBoxData(this.cropbox);
			}
		});

		//
		// Setup aspect ratio according to settings
		let aspectRatio = NaN;
		if (this.settings) {
			const { width, height } = this.settings;
			aspectRatio = width / height;
		}

		//
		// Set crop options
		// extend default with custom config
		this.cropperOptions = Object.assign({
			aspectRatio,
			movable: false,
			scalable: false,
			zoomable: false,
			viewMode: 1,
			checkCrossOrigin: true
		}, this.cropperOptions);

		//
		// Set cropperjs
		if (this.cropper) {
			if (this.lastImgSrc !== image.src) {
				this.lastImgSrc = image.src;
				this.cropper.replace(image.src);
			}
		} else {
			this.cropper = new Cropper(image, this.cropperOptions);
		}
	}

	/**
     * Image load error
     * @param event 
     */
	public imageLoadError(event: any): void {

		//
		// Set load error state
		this.loadError = true;

		//
		// Unset loading state
		this.isLoading = false;
	}

	/**
     * Export canvas
     * @param base64 
     */
	public exportCanvas(base64?: any): void {

		//
		// Get and set image, crop and canvas data
		const imageData = this.cropper.getImageData();
		const cropData = this.cropper.getCropBoxData();
		const canvas = this.cropper.getCroppedCanvas();
		const data = { imageData, cropData };

		//
		// Create promise to resolve canvas data
		const promise = new Promise(resolve => {

			//
			// Validate base64
			if (base64) {

				//
				// Resolve promise with dataUrl
				return resolve({
					dataUrl: canvas.toDataURL('image/png')
				});
			}
			canvas.toBlob(blob => resolve({ blob }));
		});

		//
		// Emit export data when promise is ready
		promise.then(res => {
			this.export.emit(Object.assign(data, res));
		});
	}
}
